import { Button } from "@/components/ui/button";
import { Link } from "react-router-dom";
import { SEO } from "@/components/SEO";
import { 
  BookOpen, 
  Lightbulb, 
  MessageSquare, 
  ArrowRight, 
  CheckCircle2, 
  Zap,
  Target,
  TrendingUp,
  Users,
  FileText,
  BarChart3
} from "lucide-react";
import logo from "@/assets/gc-content-ai-logo.png";

const Landing = () => {
  const features = [
    {
      icon: BookOpen,
      title: "Smart Knowledge Base",
      description: "Upload documents, videos, blogs, and previous posts to build a comprehensive content library that powers your AI.",
      color: "from-blue-500 to-cyan-500"
    },
    {
      icon: Lightbulb,
      title: "AI Content Strategist",
      description: "Generate content strategies with 20 unique post ideas. Smart duplicate detection ensures fresh angles every time.",
      color: "from-purple-500 to-pink-500"
    },
    {
      icon: MessageSquare,
      title: "Engagement Manager",
      description: "AI-powered comments that match your voice, build authority, and drive meaningful engagement on LinkedIn.",
      color: "from-orange-500 to-red-500"
    },
    {
      icon: FileText,
      title: "Post Generator",
      description: "Create multiple variations of LinkedIn posts in your unique tone, optimized for engagement and conversions.",
      color: "from-green-500 to-emerald-500"
    },
    {
      icon: BarChart3,
      title: "Analytics Dashboard",
      description: "Track your content performance, export data, and get insights to optimize your LinkedIn strategy.",
      color: "from-indigo-500 to-blue-500"
    },
    {
      icon: Zap,
      title: "Workflow Automation",
      description: "Streamline your content creation process from ideation to publishing with intelligent automation.",
      color: "from-yellow-500 to-orange-500"
    }
  ];

  const benefits = [
    "Save 10+ hours per week on content creation",
    "Maintain consistent voice across all posts",
    "Never run out of content ideas",
    "Build authority with smart engagement",
    "Data-driven content strategy",
    "Scale your LinkedIn presence"
  ];

  const stats = [
    { icon: Users, value: "3x", label: "More Engagement" },
    { icon: TrendingUp, value: "10x", label: "Faster Creation" },
    { icon: Target, value: "100%", label: "Your Voice" },
  ];

  return (
    <>
      <SEO 
        title="AI-Powered LinkedIn Content Strategy & Post Generator"
        description="Transform your LinkedIn presence with AI-powered content strategy, post generation, and engagement management. Create compelling content 10x faster while maintaining your unique voice."
        keywords="LinkedIn AI, LinkedIn content generator, AI post generator, LinkedIn strategy, content marketing, social media AI, LinkedIn automation, content creation tool, LinkedIn engagement"
      />
      <div className="min-h-screen bg-gradient-to-b from-background via-primary/5 to-background">
      {/* Header */}
      <header className="sticky top-0 z-50 glass-effect border-b border-border/50 backdrop-blur-md">
        <nav className="container mx-auto px-4 py-5" role="navigation" aria-label="Main navigation">
          <div className="flex items-center justify-between">
            <Link to="/" className="flex items-center gap-3 group">
              <img src={logo} alt="GC Content AI" className="h-10 group-hover:scale-105 transition-transform" />
            </Link>
            <nav className="flex items-center gap-8">
              <Link 
                to="/blog" 
                className="text-lg font-semibold text-foreground hover:text-primary transition-colors relative after:absolute after:bottom-0 after:left-0 after:h-0.5 after:w-0 after:bg-primary after:transition-all hover:after:w-full"
              >
                Blog
              </Link>
              <Link to="/auth">
                <Button size="lg" className="shadow-lg hover:shadow-xl transition-all font-semibold">
                  Get Started <ArrowRight className="ml-2 h-4 w-4" aria-hidden="true" />
                </Button>
              </Link>
            </nav>
          </div>
        </nav>
      </header>

      {/* Hero Section */}
      <section className="container mx-auto px-4 py-20 md:py-32">
        <div className="text-center max-w-4xl mx-auto animate-fade-in">
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-primary/10 text-primary font-medium text-sm mb-6">
            <img src={logo} alt="GC Content AI" className="h-5" />
            <span>AI-Powered LinkedIn Content Strategy</span>
          </div>
          
          <h1 className="text-5xl md:text-7xl font-bold mb-6 bg-gradient-to-r from-primary via-primary/80 to-primary/60 bg-clip-text text-transparent leading-tight">
            AI-Powered LinkedIn Content Strategy & Post Generator
          </h1>
          
          <p className="text-xl md:text-2xl text-muted-foreground mb-8 max-w-3xl mx-auto">
            Create engaging content, build authority, and scale your LinkedIn presence 
            with our AI-powered content strategist and engagement manager.
          </p>

          <div className="flex flex-col sm:flex-row gap-4 justify-center mb-12">
            <Link to="/auth">
              <Button size="lg" className="text-lg px-8 py-6 shadow-2xl hover:shadow-primary/50 transition-all">
                Start Creating Content <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            </Link>
            <Button size="lg" variant="outline" className="text-lg px-8 py-6">
              Watch Demo
            </Button>
          </div>

          {/* Stats */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-3xl mx-auto">
            {stats.map((stat, index) => (
              <article 
                key={index}
                className="p-6 rounded-xl glass-effect border border-border/50 card-hover"
                style={{ animationDelay: `${index * 0.1}s` }}
              >
                <stat.icon className="h-8 w-8 text-primary mb-3 mx-auto" aria-hidden="true" />
                <div className="text-4xl font-bold mb-1 bg-gradient-to-r from-primary to-primary/60 bg-clip-text text-transparent">
                  {stat.value}
                </div>
                <div className="text-sm text-muted-foreground">{stat.label}</div>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* Features Grid */}
      <section className="container mx-auto px-4 py-20 bg-gradient-to-b from-background to-primary/5">
        <div className="text-center mb-16">
          <h2 className="text-4xl md:text-5xl font-bold mb-4">
            Everything You Need to Dominate LinkedIn
          </h2>
          <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
            A complete suite of AI-powered tools to create, manage, and optimize your LinkedIn content strategy
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-7xl mx-auto">
          {features.map((feature, index) => (
            <article 
              key={index}
              className="p-6 rounded-2xl glass-effect border border-border/50 card-hover group"
              style={{ animationDelay: `${index * 0.1}s` }}
            >
              <div className={`inline-flex p-4 rounded-xl bg-gradient-to-br ${feature.color} mb-4 group-hover:scale-110 transition-transform`}>
                <feature.icon className="h-6 w-6 text-white" aria-hidden="true" />
              </div>
              <h3 className="text-xl font-bold mb-2">{feature.title}</h3>
              <p className="text-muted-foreground">{feature.description}</p>
            </article>
          ))}
        </div>
      </section>

      {/* Benefits Section */}
      <section className="container mx-auto px-4 py-20">
        <div className="max-w-4xl mx-auto glass-effect p-12 rounded-3xl border border-border/50">
          <h2 className="text-4xl font-bold mb-8 text-center">Why Choose LinkedIn Content AI?</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {benefits.map((benefit, index) => (
              <div 
                key={index} 
                className="flex items-start gap-3 p-4 rounded-lg hover:bg-accent/50 transition-colors"
              >
                <CheckCircle2 className="h-6 w-6 text-primary flex-shrink-0 mt-0.5" />
                <span className="text-lg">{benefit}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="container mx-auto px-4 py-20">
        <div className="max-w-4xl mx-auto text-center p-12 rounded-3xl bg-gradient-to-br from-primary to-primary/80 text-white">
          <h2 className="text-4xl md:text-5xl font-bold mb-4">
            Ready to Transform Your LinkedIn Strategy?
          </h2>
          <p className="text-xl mb-8 text-white/90">
            Join thousands of professionals creating better content, faster.
          </p>
          <Link to="/auth">
            <Button size="lg" variant="secondary" className="text-lg px-8 py-6 shadow-2xl">
              Get Started Free <ArrowRight className="ml-2 h-5 w-5" />
            </Button>
          </Link>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-border/50 py-12" role="contentinfo">
        <div className="container mx-auto px-4">
          <div className="flex flex-col md:flex-row justify-between items-center gap-4">
            <div className="flex items-center gap-3">
              <img src={logo} alt="GC Content AI" className="h-8" />
            </div>
            <p className="text-sm text-muted-foreground">
              © 2025 GC Content AI. All rights reserved.
            </p>
          </div>
        </div>
      </footer>
    </div>
    </>
  );
};

export default Landing;